/**
 * The student's OWN completion progress in a cohort (M1). Reads the
 * server-authoritative state (`/api/cohorts/:id/my-completion`) and shows chips per
 * criterion ("Attendance 80% ✓ · 3/4 challenges · Final project ✗").
 */
import { type Component, createResource, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { completionChips } from "@space/shared";

import { useI18n } from "@/providers/i18n-provider";
import { fetchMyCompletion } from "./api";

export const CompletionProgress: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  // Errors (e.g. not yet graded / not active) → simply not shown.
  const [status] = createResource(
    () => props.cohortId,
    async (id) => fetchMyCompletion(id).catch(() => null),
  );

  return (
    <Show when={status()}>
      {(s) => (
        <div class="flex flex-wrap items-center gap-1.5 text-xs" data-testid="my-completion">
          <span
            class={s().passed ? "font-medium text-success" : "text-muted-foreground"}
            data-testid="my-completion-passed"
            data-passed={s().passed ? "true" : "false"}
          >
            {s().passed ? t(msg`Requirements met`) : t(msg`In progress`)}
          </span>
          <For each={completionChips(s())}>
            {(chip) => (
              <span
                class={`rounded px-1.5 py-0.5 ${
                  chip.met ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
                }`}
                data-testid={`my-completion-chip-${chip.key}`}
              >
                {chip.shortLabel} {chip.progress} {chip.met ? "✓" : "✗"}
              </span>
            )}
          </For>
        </div>
      )}
    </Show>
  );
};
